import { ref, computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { uniq } from "@utils/toolBox";
import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
import useMyFetch from "@stores/useMyFetch";
import { getChatAppBaseUrl } from "@utils/constants/chatUrls";
import { DEFAULT_CHAT_PAGE_EXTERNAL_ID } from "@utils/constants/chatPages";
import type { SelectOption } from "@/components/episto-ui/select";
import type { ChatPage, Survey } from "@/types";

export interface SurveyShareForm {
  country: string;
  language: string;
  page: string;
  testLink: string;
  liveLink: string;
}

type SurveyChatPage = Pick<ChatPage, "id" | "token" | "name">;

export const useSurveyShare = (survey: Survey, formVal: SurveyShareForm, localeOptions: Ref<SelectOption[]>) => {
  const { countryOptions } = storeToRefs(useSurveyTargetsStore());

  const surveyChatToken = ref(survey.chat_token);
  const defaultCountry = ref(survey.targets[0].countries?.[0]);
  const defaultLanguage = ref(String(localeOptions.value[0].value));
  const targetCountries = computed(() => uniq(survey.targets?.map((t) => t.countries).flat()));
  const targetCountryOptions = computed<SelectOption[]>(() =>
    uniq(countryOptions.value.filter(({ value }) => targetCountries.value.includes(value))),
  );

  const languageId = computed<string | null>(() => {
    const locale = Object.values(survey.locale_by_ids).find((locale) => locale.token === formVal.language);
    return locale ? locale.language : null;
  });

  const surveyChatPages = ref<SurveyChatPage[]>([]);
  const defaultChatPageToken = ref<string | null>(null);
  const hasCustomPages = computed(() => surveyChatPages.value.length > 0);
  // The account default when it has one, the Episto page otherwise — links omit `p` for it
  const effectiveDefaultPage = computed(() => defaultChatPageToken.value ?? DEFAULT_CHAT_PAGE_EXTERNAL_ID);
  const pageOptions = computed<SelectOption[]>(() =>
    [
      { value: DEFAULT_CHAT_PAGE_EXTERNAL_ID, label: "Episto" },
      ...surveyChatPages.value.map((chatPage) => ({ value: chatPage.token, label: chatPage.name })),
    ].map((option) =>
      option.value === effectiveDefaultPage.value ? { ...option, label: `${option.label} (Default)` } : option,
    ),
  );

  const fetchSurveyChatPages = async (): Promise<void> => {
    const { data } = await useMyFetch(`/surveys/${survey.token}/chat_pages`).json<{
      chat_pages: SurveyChatPage[];
      default_chat_page_token: string | null;
    }>();
    surveyChatPages.value = data.value?.chat_pages || [];
    defaultChatPageToken.value = data.value?.default_chat_page_token ?? null;
    formVal.page = effectiveDefaultPage.value;
  };

  const chatBaseURL = computed(() => getChatAppBaseUrl());

  const constructUrl = (country: string, languageCode: string, type: "test" | "live"): string => {
    const base = chatBaseURL.value + "/?";
    const params = `s=${surveyChatToken.value}&lg=${languageId.value}-${languageCode}&cc=${country}`;
    const pageParam = formVal.page && formVal.page !== effectiveDefaultPage.value ? `&p=${formVal.page}` : "";
    const url_suffix = type === "test" ? "&test=1" : "";
    return `${base}${params}${pageParam}${url_suffix}`;
  };

  const initForm = (): void => {
    formVal.country = defaultCountry.value;
    formVal.language = defaultLanguage.value;
    formVal.page = effectiveDefaultPage.value;
    formVal.testLink = constructUrl(defaultCountry.value, defaultLanguage.value, "test");
    formVal.liveLink = constructUrl(defaultCountry.value, defaultLanguage.value, "live");
  };

  return {
    targetCountryOptions,
    constructUrl,
    initForm,
    languageId,
    hasCustomPages,
    pageOptions,
    fetchSurveyChatPages,
  };
};
